import type { HoaDonTabSession, VoucherType } from '@/redux/ducks/hoaDonPosDucks'
import routerConstants from '@/constants/routerConstants'

/** Query: loại form POS (`hd` | `dh` | `ycsc` | `th`). */
export const BAN_HANG_URL_LOAI = 'loai'

/** Query: index 1-based trong các tab cùng loại. */
export const BAN_HANG_URL_INDEX = 'index'

/** Mã ngắn trên URL ↔ voucherType nội bộ. */
export type BanHangUrlLoaiCode = 'hd' | 'dh' | 'ycsc' | 'th'

const LOAI_TO_CODE: Record<VoucherType, BanHangUrlLoaiCode> = {
  hoaDon: 'hd',
  datHang: 'dh',
  yeuCauSuaChua: 'ycsc',
  traHang: 'th',
}

const CODE_TO_LOAI: Record<BanHangUrlLoaiCode, VoucherType> = {
  hd: 'hoaDon',
  dh: 'datHang',
  ycsc: 'yeuCauSuaChua',
  th: 'traHang',
}

/** Parse `loai` — nhận mã ngắn; vẫn nhận tên đầy đủ cũ để tương thích. */
export const parseBanHangUrlLoai = (raw: string | null | undefined): VoucherType | null => {
  if (!raw) return null
  const key = raw.trim().toLowerCase()
  if (key in CODE_TO_LOAI) return CODE_TO_LOAI[key as BanHangUrlLoaiCode]
  if (raw === 'hoaDon' || raw === 'datHang' || raw === 'yeuCauSuaChua' || raw === 'traHang') {
    return raw
  }
  return null
}

export const banHangUrlLoaiCode = (loai: VoucherType): BanHangUrlLoaiCode => LOAI_TO_CODE[loai]

export const parseBanHangUrlIndex = (raw: string | null | undefined): number | null => {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) return null
  return n
}

/** Số cuối title tab mới ("Đặt hàng 3" → 3). */
const newTabTitleSeq = (tab: HoaDonTabSession): number | null => {
  if (tab.type !== 'new') return null
  const m = /(\d+)\s*$/.exec(tab.title)
  return m ? Number(m[1]) : null
}

/**
 * `index` trên URL: tab mới = số trên title (khớp "Đặt hàng 3" ↔ `index=3`);
 * tab khác (sửa / sao chép…) = vị trí 1-based trong nhóm cùng `voucherType`.
 */
export const banHangTabIndexAmongLoai = (
  tabs: HoaDonTabSession[],
  tab: HoaDonTabSession,
): number => {
  const seq = newTabTitleSeq(tab)
  if (seq != null) return seq
  const ofType = tabs.filter((t) => t.voucherType === tab.voucherType)
  const idx = ofType.findIndex((t) => t.tabId === tab.tabId)
  return idx >= 0 ? idx + 1 : 1
}

export const findBanHangTabByLoaiIndex = (
  tabs: HoaDonTabSession[],
  loai: VoucherType,
  index: number,
): HoaDonTabSession | null => {
  const ofType = tabs.filter((t) => t.voucherType === loai)
  if (ofType.length === 0) return null
  const bySeq = ofType.find((t) => newTabTitleSeq(t) === index)
  if (bySeq) return bySeq
  return ofType[Math.min(Math.max(index, 1), ofType.length) - 1] ?? null
}

/** Deep-link tạo/sửa tab — không ghi đè bằng `loai`/`index`. */
export const banHangUrlHasActionDeepLink = (params: URLSearchParams): boolean =>
  Boolean(
    params.get('newVoucher') ||
      params.get('editVoucher') ||
      params.get('fromDatHang') ||
      params.get('fromDonHang'),
  )

export const buildBanHangPosSearch = (loai: VoucherType, index: number): string => {
  const params = new URLSearchParams()
  params.set(BAN_HANG_URL_LOAI, banHangUrlLoaiCode(loai))
  params.set(BAN_HANG_URL_INDEX, String(index))
  return `?${params.toString()}`
}

const LIST_PATH_PREFIX_TO_LOAI: ReadonlyArray<[string, VoucherType]> = [
  [`/${routerConstants.datHang.name}`, 'datHang'],
  [`/${routerConstants.traHang.name}`, 'traHang'],
  [`/${routerConstants.yeuCauSuaChua.name}`, 'yeuCauSuaChua'],
]

/** Loại phiếu POS ứng với màn đang đứng (menu Đặt hàng → `datHang`…); mặc định hóa đơn. */
export const banHangVoucherTypeFromPath = (pathname: string): VoucherType => {
  const path = pathname.endsWith('/') ? pathname : `${pathname}/`
  const hit = LIST_PATH_PREFIX_TO_LOAI.find(([prefix]) => path.startsWith(prefix))
  return hit ? hit[1] : 'hoaDon'
}

/** URL nút "Bán hàng": chỉ có `loai`, không `index` → POS mở tab nháp cùng loại hoặc tạo mới. */
export const buildBanHangPosEntryUrl = (loai: VoucherType): string => {
  const params = new URLSearchParams()
  params.set(BAN_HANG_URL_LOAI, banHangUrlLoaiCode(loai))
  return `${routerConstants.banHang}?${params.toString()}`
}

/** Tab nháp (tạo mới, chưa lưu) cùng loại — ưu tiên tab đang active. */
export const findBanHangDraftTab = (
  tabs: HoaDonTabSession[],
  loai: VoucherType,
  activeTabId: string | null,
): HoaDonTabSession | null => {
  const drafts = tabs.filter((t) => t.voucherType === loai && t.type === 'new')
  return drafts.find((t) => t.tabId === activeTabId) ?? drafts[0] ?? null
}
